/**
 * Progression → **Activité** : ce que la montre a enregistré
 * (`ARCHITECTURE_V2.md` §5.1 à §5.3 ; specs §7.3, §8.2, §12.4).
 *
 * Le §8.2 des specs range les « activités Coros » dans Progression, et l'usage
 * le confirme : c'est l'onglet où l'on vient regarder ce qui a bougé sur
 * plusieurs jours, pas décider quelque chose maintenant.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🔴 LE MUR DE NEUTRALITÉ (§5.3) — vu depuis l'écran
 * ══════════════════════════════════════════════════════════════════════════
 *
 * C'est **le** sujet de cet écran, et il se tient à trois endroits :
 *
 *   1. **Les kcal dépensées et consommées sont côte à côte, jamais
 *      soustraites.** Il n'existe aucun « restant ajusté », aucune différence
 *      affichée, et le contrat d'API lui-même n'a pas de champ pour en porter
 *      une (`IntervalsDayDTO` n'a que `expendedKcal` et `consumedKcal`). Une
 *      note sous la liste le dit en toutes lettres, parce qu'un lecteur qui voit
 *      deux nombres fait spontanément la soustraction — autant écrire pourquoi
 *      l'application ne la fait pas.
 *   2. **Le poids importé est marqué comme indicatif** partout où il apparaît.
 *      Il n'entre jamais dans `weight_entry`, donc ni dans la moyenne lissée, ni
 *      dans la tendance, ni dans le rappel de pesée (§5.3).
 *   3. **Rien n'est saisissable.** L'écran ne porte aucune mutation : pas de
 *      correction, pas de bouton de synchronisation, pas de champ de clé. Ce
 *      sont des mesures venues d'ailleurs ; on les regarde.
 *
 * ⚠️ **Aucun conseil, aucune conclusion, aucune couleur de jugement** — la règle
 * générale du §6.2 de la V2, qui court depuis l'écart kcal de la V1 (specs
 * §6.2) : « l'application mesure ; l'utilisateur conclut ». Une VFC basse
 * n'est pas peinte en rouge.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  IntervalsDayDTO,
  IntervalsStatusDTO,
  IntervalsWellnessDTO,
} from '../../../../shared/types';
import { readError } from '../../api/client';
import { fetchIntervals, INTERVALS_KEY, syncIntervals } from '../../api/intervals';
import { Button, Card } from '../../components/nourri';
import { ErrorNote, Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { HealthStatsSection } from './HealthStatsSection';
import {
  ABSENT,
  formatCount,
  formatDecimal,
  formatDistance,
  formatDuration,
  formatElevation,
  formatHr,
  formatKcal,
  formatSleep,
  formatStartTime,
  formatSyncedAt,
  formatType,
} from './activityFields';
import { formatDayRow } from './weightFields';

/* ==========================================================================
 * Statut
 * ======================================================================= */

/**
 * Ce que les Réglages affichent aussi, en plus court.
 *
 * ⚠️ **« Non configuré » n'est pas une erreur**, et ne se peint pas comme telle.
 * C'est une fonction qu'on n'utilise pas — exactement le statut
 * `offStatus: 'unavailable'` de l'étape 6, « une information portée par la
 * réponse, jamais une erreur ».
 */
function StatusNote({ status }: { status: IntervalsStatusDTO }) {
  if (!status.configured) {
    return (
      <div className="n-empty">
        <p style={{ margin: 0, fontWeight: 600 }}>Synchronisation non configurée</p>
        <p className="n-hint" style={{ marginTop: 8 }}>
          Aucune clé intervals.icu n’est posée sur le serveur. La clé est un secret
          serveur : elle ne se saisit pas ici, elle s’installe une fois par{' '}
          <code>infra/set-intervals-env.sh</code>.
        </p>
      </div>
    );
  }

  return (
    <p className="n-foot-note">
      Dernière synchronisation {formatSyncedAt(status.lastSyncAt)}
      {status.lastSyncOk === false && status.lastError !== null && (
        <>
          {' '}
          — <strong>échec</strong> : {status.lastError}. Nouvelle tentative dans moins de
          30 minutes.
        </>
      )}
      {status.lastSyncOk === true && (
        <>
          {' '}
          · {formatCount(status.lastActivityCount)} activité(s) et{' '}
          {formatCount(status.lastWellnessCount)} jour(s) de santé relus sur{' '}
          {formatCount(status.windowDays)} jours
        </>
      )}
    </p>
  );
}

/* ==========================================================================
 * Santé du jour
 * ======================================================================= */

/** Un chiffre de la rangée — repris tel quel de l'écran de pesée. */
function Figure({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div>
      <div className="n-stat__lbl">{label}</div>
      <div className="n-stat__val tnum">
        {value}
        {unit !== undefined && <span className="n-stat__unit">{unit}</span>}
      </div>
    </div>
  );
}

/**
 * Le dernier relevé de santé disponible.
 *
 * ⚠️ **Le plus récent DISPONIBLE, pas celui d'aujourd'hui.** Les données de
 * santé arrivent avec retard — c'est tout le motif de la fenêtre glissante de
 * quatorze jours (§5.2) : « une activité importée le matin voit sa VFC arriver
 * le soir ». Exiger la date du jour afficherait un écran vide chaque matin. La
 * carte porte donc sa date, sans quoi on lirait un chiffre d'avant-hier en
 * croyant qu'il est de ce matin.
 */
function WellnessCard({ wellness }: { wellness: IntervalsWellnessDTO }) {
  return (
    <Card>
      <div className="n-act-head">
        <span className="n-act-head__t">Santé</span>
        <span className="n-act-head__d">{formatDayRow(wellness.date)}</span>
      </div>

      <div className="n-stat-row" style={{ marginTop: 10 }}>
        <Figure label="FC repos" value={formatCount(wellness.restingHr)} unit="bpm" />
        <Figure label="VFC" value={formatCount(wellness.hrv)} unit="ms" />
        <Figure label="Sommeil" value={formatSleep(wellness.sleepSeconds)} />
      </div>

      <div className="n-stat-row" style={{ marginTop: 12 }}>
        <Figure label="Forme" value={formatDecimal(wellness.form)} />
        <Figure label="Fitness" value={formatDecimal(wellness.ctl)} />
        <Figure label="Fatigue" value={formatDecimal(wellness.atl)} />
      </div>

      <div className="n-stat-row" style={{ marginTop: 12 }}>
        <Figure label="Pas" value={formatCount(wellness.steps)} />
        <Figure label="Score sommeil" value={formatCount(wellness.sleepScore)} />
        {/*
          🔴 **Le poids importé, et sa mention obligatoire.** Le §5.3 nomme ce
          champ comme « le piège précis » : il n'est jamais écrit dans
          `weight_entry`, donc il n'entre ni dans la moyenne lissée, ni dans la
          tendance, ni dans la condition du rappel de pesée. L'afficher sans le
          dire ferait croire à une pesée enregistrée — et laisserait chercher
          longtemps pourquoi la courbe ne bouge pas.
        */}
        <Figure label="Poids montre" value={formatDecimal(wellness.weightKg)} unit="kg" />
      </div>

      <p className="n-hint" style={{ marginTop: 12 }}>
        Le poids de la montre est <strong>indicatif</strong> : il n’est pas enregistré
        comme pesée et n’entre ni dans la moyenne lissée, ni dans la tendance. La pesée
        du projet se fait le matin, à jeun.
      </p>
    </Card>
  );
}

/* ==========================================================================
 * Une journée
 * ======================================================================= */

function DayCard({ day }: { day: IntervalsDayDTO }) {
  return (
    <Card>
      <div className="n-act-head">
        <span className="n-act-head__t">{formatDayRow(day.date)}</span>
      </div>

      {day.activities.length === 0 ? (
        <p className="n-hint" style={{ marginTop: 8 }}>
          Aucune activité — seules des données de santé ont été relevées ce jour-là.
        </p>
      ) : (
        <ul className="n-list" style={{ marginTop: 6 }}>
          {day.activities.map((activity) => (
            <li key={activity.id}>
              <div className="n-act-row">
                <div className="n-act-row__main">
                  <span className="n-act-row__name">
                    {activity.name ?? formatType(activity.type)}
                  </span>
                  <span className="n-act-row__meta">
                    {formatType(activity.type)}
                    {activity.startAt !== null && <> · {formatStartTime(activity.startAt)}</>}
                    {activity.load !== null && <> · charge {formatCount(activity.load)}</>}
                  </span>
                </div>
                {/*
                  ⚠️ Chiffres tabulaires et `nowrap` : quatre valeurs sur une
                  colonne étroite, c'est très exactement le défaut corrigé sur
                  les jauges de repas à l'étape 7 — deux valeurs qui s'allongent
                  finissent par se chevaucher.
                */}
                <div className="n-act-row__figs tnum">
                  <span>{formatDuration(activity.durationSeconds)}</span>
                  {activity.distanceM !== null && <span>{formatDistance(activity.distanceM)}</span>}
                  {activity.elevationM !== null && (
                    <span>{formatElevation(activity.elevationM)} D+</span>
                  )}
                  {activity.averageHr !== null && <span>{formatHr(activity.averageHr)}</span>}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/*
        🔴 **Les deux nombres, CÔTE À CÔTE.** C'est le §5.3 rendu littéralement :
        « les calories dépensées sont affichées à côté des calories consommées,
        jamais soustraites ». Il n'y a pas de troisième nombre, et le DTO n'a pas
        de champ pour en porter un.

        ⚠️ « — » et non « 0 » quand la donnée manque : un jour sans saisie au
        journal n'est pas un jour sans manger.
      */}
      <div className="n-act-kcal">
        <span>
          Dépensé <strong className="tnum">{formatKcal(day.expendedKcal)}</strong>
        </span>
        <span className="n-act-kcal__sep" aria-hidden="true">
          ·
        </span>
        <span>
          Consommé{' '}
          <strong className="tnum">
            {day.consumedKcal === null ? ABSENT : formatKcal(day.consumedKcal)}
          </strong>
        </span>
      </div>
    </Card>
  );
}

/* ==========================================================================
 * Écran
 * ======================================================================= */

export function ActivitySection() {
  const queryClient = useQueryClient();

  /*
   * ⚠️ `staleTime` généreux : l'ingestion tourne toutes les trente minutes côté
   * serveur, donc rafraîchir plus souvent que ça ne peut rien apprendre de neuf.
   * Ce n'est pas une optimisation de confort — c'est ce qui évite qu'un écran
   * ouvert en salle relance une requête à chaque retour au premier plan, là où
   * il n'y a pas de réseau.
   */
  const intervals = useQuery({
    queryKey: INTERVALS_KEY,
    queryFn: fetchIntervals,
    staleTime: 5 * 60 * 1000,
  });

  /**
   * 🔴 **Le bouton de synchronisation vit ICI aussi, pas seulement aux
   * Réglages** *(demandé après usage, 05/09/2026)*.
   *
   * C'est le geste du retour de sortie : on ouvre l'onglet Activité pour voir ce
   * qu'on vient de faire, et l'ingestion de fond n'est pas encore passée. Le
   * chercher dans les Réglages demanderait de quitter l'écran qu'on regarde,
   * pour revenir dessus. Les Réglages gardent le leur : il y sert au moment où
   * l'on pose une clé, c'est-à-dire quand cet écran-ci est vide.
   *
   * ⚠️ **Aucune mise à jour optimiste**, et ce n'est pas un oubli : on ne peut
   * pas prévoir ce qu'un tiers va rendre. La réponse porte l'état complet
   * recalculé, comme pour la pesée (étape 8 de la V1).
   */
  const synchroniser = useMutation({
    mutationFn: syncIntervals,
    onSuccess: (response) => {
      queryClient.setQueryData(INTERVALS_KEY, response);
    },
  });

  const lecture = readError(intervals);
  const data = intervals.data;

  /*
   * Le relevé de santé le plus récent de la fenêtre — cf. `WellnessCard`.
   * ⚠️ `days` est déjà trié du plus récent au plus ancien par le serveur : le
   * premier qui en porte un est le bon, sans avoir à retrier ici.
   */
  const latestWellness = data?.days.find((day) => day.wellness !== null)?.wellness ?? null;

  return (
    <>
      {/*
        🔴 Erreur de lecture **après** les données, jamais avant (V2 §3.5). Hors
        ligne, la fenêtre déjà en cache doit continuer de s'afficher — c'est le
        défaut de fond trouvé à l'usage à l'étape 2, « six écrans testaient
        l'erreur avant les données ».
      */}
      <ReadNote error={lecture} quoi="les activités importées" />
      {data === undefined && lecture === null && <Hint>Chargement…</Hint>}

      {data && (
        <>
          {/*
            ⚠️ L'échec d'une synchronisation manuelle est annoncé, mais **il ne
            remplace pas l'écran** : les activités déjà importées restent
            affichées dessous. C'est la même séparation lecture/écriture que
            partout depuis le §3.5 — un bandeau rouge par-dessus des données
            parfaitement bonnes serait un mensonge.
          */}
          {synchroniser.error && <ErrorNote>{synchroniser.error.message}</ErrorNote>}

          <StatusNote status={data.status} />

          {/*
            ⚠️ Désactivé sans clé : il n'y aurait rien à demander à personne, et
            un bouton qui répond « rien fait » apprend surtout à ne plus s'en
            servir. Le message de `StatusNote` dit alors quoi faire.
          */}
          {data.status.configured && (
            <Button
              variant="secondary"
              disabled={synchroniser.isPending}
              onClick={() => {
                synchroniser.mutate();
              }}
            >
              {synchroniser.isPending ? 'Synchronisation…' : 'Synchroniser maintenant'}
            </Button>
          )}

          {latestWellness !== null && <WellnessCard wellness={latestWellness} />}

          {data.status.configured && data.days.length === 0 && (
            <div className="n-empty">
              Aucune activité ni donnée de santé sur les {formatCount(data.status.windowDays)}{' '}
              derniers jours.
            </div>
          )}

          {data.days.map((day) => (
            <DayCard key={day.date} day={day} />
          ))}

          {data.days.length > 0 && (
            <p className="n-foot-note">
              Dépense et consommation sont affichées côte à côte, jamais soustraites :
              aucune donnée importée n’ajuste un objectif de macros.
            </p>
          )}

          {/*
            🔴 **Les séries et les croisements viennent APRÈS le détail des
            quatorze jours** (V2 étape 9). L'ordre n'est pas indifférent : ce
            qu'on vient chercher en ouvrant cet onglet, c'est ce qu'on a fait
            ces jours-ci — la mise en série répond à une autre question, qu'on
            se pose moins souvent. C'est l'arbitrage de fréquence déjà tenu par
            l'ordre des blocs de l'écran de poids.

            ⚠️ Cette section porte **sa propre plage** et n'a donc rien à voir
            avec la fenêtre de quatorze jours au-dessus, qui est celle de
            l'ingestion (§5.2) et ne se règle pas.
          */}
          <HealthStatsSection />
        </>
      )}
    </>
  );
}
